import React, { useState, useEffect } from 'react';
import { FaHistory, FaChevronDown, FaChevronUp, FaSpinner, FaWeight } from 'react-icons/fa';
import { dashboardAPI } from '../../services/dashboardAPI';

const moodEmojis = {
  1: '😞',
  2: '😕',
  3: '😐',
  4: '🙂',
  5: '😄',
};

const CheckInHistory = () => {
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const data = await dashboardAPI.getCheckInHistory();
      setCheckIns(data);
    } catch (error) {
      console.error('Failed to fetch check-in history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
          <FaHistory className="text-xl text-accent-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900">Check-in History</h3>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <FaSpinner className="animate-spin text-2xl text-gray-400" />
        </div>
      ) : checkIns.length === 0 ? (
        <div className="text-center py-8 px-4 bg-gray-50 rounded-xl">
          <FaHistory className="text-4xl text-gray-300 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-700 mb-2">No Check-ins Yet</h4>
          <p className="text-gray-500 text-sm">
            Your weekly check-in history will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {checkIns.map((checkIn) => (
            <div
              key={checkIn.id}
              className="border border-gray-200 rounded-xl overflow-hidden"
            >
              {/* Header - Always visible */}
              <button
                onClick={() => toggleExpand(checkIn.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex flex-col items-center justify-center">
                    <span className="text-xs text-primary-600 font-medium">Week</span>
                    <span className="text-lg font-bold text-primary-700">{checkIn.weekNumber}</span>
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <FaWeight className="text-gray-400" />
                      <span className="font-semibold text-gray-900">{checkIn.weight} kg</span>
                      {checkIn.overallMood && (
                        <span className="text-xl">{moodEmojis[checkIn.overallMood]}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{formatDate(checkIn.submittedAt)}</p>
                  </div>
                </div>
                {expandedId === checkIn.id ? (
                  <FaChevronUp className="text-gray-400" />
                ) : (
                  <FaChevronDown className="text-gray-400" />
                )}
              </button>

              {/* Expanded Details */}
              {expandedId === checkIn.id && (
                <div className="p-4 pt-0 border-t border-gray-100">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {checkIn.waistMeasurement && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Waist</p>
                        <p className="font-semibold text-gray-900">{checkIn.waistMeasurement} cm</p>
                      </div>
                    )}
                    {checkIn.hipMeasurement && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Hip</p>
                        <p className="font-semibold text-gray-900">{checkIn.hipMeasurement} cm</p>
                      </div>
                    )}
                    {checkIn.chestMeasurement && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Chest</p>
                        <p className="font-semibold text-gray-900">{checkIn.chestMeasurement} cm</p>
                      </div>
                    )}
                    {checkIn.sleepHours && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Sleep</p>
                        <p className="font-semibold text-gray-900">{checkIn.sleepHours} hrs</p>
                      </div>
                    )}
                    {checkIn.waterIntake && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Water</p>
                        <p className="font-semibold text-gray-900">{checkIn.waterIntake} L</p>
                      </div>
                    )}
                  </div>

                  {checkIn.exerciseDone && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Exercise</p>
                      <p className="text-sm text-gray-700">{checkIn.exerciseDone}</p>
                    </div>
                  )}

                  {checkIn.challengesFaced && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Challenges</p>
                      <p className="text-sm text-gray-700">{checkIn.challengesFaced}</p>
                    </div>
                  )}

                  {checkIn.goalsNextWeek && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Goals</p>
                      <p className="text-sm text-gray-700">{checkIn.goalsNextWeek}</p>
                    </div>
                  )}

                  {checkIn.additionalNotes && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-700">{checkIn.additionalNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CheckInHistory;
